import AppsIcon from "@mui/icons-material/AppRegistration";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import NotebookIcon from "@mui/icons-material/AutoStories";
import MaintenanceIcon from "@mui/icons-material/Construction";
import InfoIcon from "@mui/icons-material/Info";
import FileIcon from "@mui/icons-material/InsertDriveFile";
import LogoutIcon from "@mui/icons-material/Logout";
import ProfileIcon from "@mui/icons-material/ManageAccounts";
import MessageIcon from "@mui/icons-material/Message";
import NotificationsIcon from "@mui/icons-material/Notifications";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import PublicIcon from "@mui/icons-material/Public";
import FlaskIcon from "@mui/icons-material/Science";
import SystemIcon from "@mui/icons-material/SettingsInputComposite";
import OperateAsIcon from "@mui/icons-material/SupervisedUserCircleOutlined";
import AppBar from "@mui/material/AppBar";
import Avatar from "@mui/material/Avatar";
import Badge, { badgeClasses } from "@mui/material/Badge";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { menuClasses } from "@mui/material/Menu";
import Popover from "@mui/material/Popover";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import SvgIcon from "@mui/material/SvgIcon";
import { darken, lighten, useTheme } from "@mui/material/styles";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import { observer } from "mobx-react-lite";
import React from "react";
import { useTranslation } from "react-i18next";
import I18nRoot from "@/modules/common/i18n/I18nRoot";
import TransRichText from "@/modules/common/i18n/TransRichText";
import type { Person } from "@/stores/definitions/Person";
import type { URL } from "@/util/types";
import { ACCENT_COLOR as GALLERY_COLOR } from "../../assets/branding/rspace/gallery";
import { ACCENT_COLOR as INVENTORY_COLOR } from "../../assets/branding/rspace/inventory";
import { ACCENT_COLOR as OTHER_COLOR } from "../../assets/branding/rspace/other";
import { ACCENT_COLOR as SYSADMIN_COLOR } from "../../assets/branding/rspace/sysadmin";
import { ACCENT_COLOR as WORKSPACE_COLOR } from "../../assets/branding/rspace/workspace";
import JwtService from "../../common/JwtService";
import useWhoAmI from "../../hooks/api/useWhoAmI";
import useSessionStorage from "../../hooks/browser/useSessionStorage";
import useViewportDimensions from "../../hooks/browser/useViewportDimensions";
import useWebSocketNotifications from "../../hooks/websockets/useWebSocketNotifications";
import { getRelativeTime } from "../../stores/definitions/Units";
import * as FetchingData from "../../util/fetchingData";
import * as Parsers from "../../util/parsers";
import Result from "../../util/result";
import AccentMenuItem from "../AccentMenuItem";
import { AccessibilityTipsIconButton, AccessibilityTipsMenuItem } from "../AccessibilityTips";
import { Menu } from "../DialogBoundary";
import HelpDocs from "../Help/HelpDocs";
import HelpLinkIcon from "../HelpLinkIcon";
import IconButtonWithTooltip from "../IconButtonWithTooltip";
import VisuallyHiddenHeading from "../VisuallyHiddenHeading";
import AboutRSpaceDialog from "./AboutRSpaceDialog";
import type SidebarToggle from "./SidebarToggle";
import useUiNavigationData, { type UiNavigationData } from "./useUiNavigationData";

declare global {
  interface Window {
    gapi?: {
      auth2?: {
        getAuthInstance(): {
          signOut(): Promise<void>;
        };
      };
    };
  }
}
const NotificationCounter = ({ currentUser }: { currentUser: Person }) => {
  const { t } = useTranslation("common");
  const { notificationCount, messageCount, specialMessageCount } = useWebSocketNotifications(`${currentUser.id}`);
  return (
    <Box
      sx={{
        ml: 1,
      }}
      role="status"
      aria-live="polite"
      aria-relevant="text"
      aria-label={t("appBar.notificationsAndMessages")}
    >
      <Badge
        badgeContent={notificationCount + messageCount + specialMessageCount}
        color="error"
        sx={{
          [`& .${badgeClasses.badge}`]: {
            transform: "translate(17%, -19%)",
          },
        }}
      >
        <IconButtonWithTooltip
          size="small"
          href="/dashboard"
          component="a"
          sx={{
            cursor: "pointer",
          }}
          icon={
            notificationCount + messageCount + specialMessageCount > 0 ? (
              <NotificationsIcon />
            ) : (
              <NotificationsNoneIcon />
            )
          }
          title={t("appBar.notifications")}
        />
      </Badge>
    </Box>
  );
};
const IncomingMaintenancePopup = ({ startDate }: { startDate: Date }) => {
  const { t } = useTranslation("common");
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const popoverId = React.useId();
  const message = t("appBar.maintenancePopup", { relativeTime: getRelativeTime(startDate) });

  /*
   * On the non-react parts of the product, the app bar is rendered inside of a
   * shadow root to prevent the styles of the app bar from leaking into the
   * rest of the page. It is important that the popover is rendered inside of
   * the shadow root so that it is styled correctly. To do this, we pass a
   * reference to this wrapper div.
   */
  const ref = React.useRef<HTMLDivElement>(null);
  return (
    <div ref={ref}>
      <IconButton
        onClick={(e) => {
          setAnchorEl(e.currentTarget);
        }}
        aria-label={message}
        aria-controls={popoverId}
        aria-haspopup="dialog"
        color="error"
      >
        <MaintenanceIcon
          sx={{
            color: "inherit !important",
          }}
        />
      </IconButton>
      <Popover
        container={ref.current}
        id={popoverId}
        open={Boolean(anchorEl)}
        role="dialog"
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Typography
          sx={{
            p: 2,
          }}
        >
          {/*
           * We show a relative time here rather than an absolute time to avoid
           * the need to take into account the user's timezone.
           */}
          {message}
        </Typography>
      </Popover>
    </div>
  );
};
const StyledAvatar = ({
  size,
  sx,
  ...props
}: {
  size?: "small";
} & React.ComponentPropsWithoutRef<typeof Avatar>): React.ReactNode => (
  <Avatar
    {...props}
    sx={{
      color: "#fce8f0",
      backgroundColor: "#ed1064",
      ...(size === "small"
        ? {
            height: "24px",
            width: "24px",
            fontSize: "0.8em",
          }
        : {}),
      ...sx,
    }}
  />
);

/**
 * This image is used to indicate the current user
 * - If the user has uploaded a profile image then it is shown
 * - Otherwise a magenta circle with their first initial is shown
 * - If they're operating as another user then a red icon mark is
 *   shown
 */
const DynamicAvatar = ({
  uiNavigationData,
  size,
}: {
  uiNavigationData: FetchingData.Fetched<UiNavigationData>;
  size?: "small";
}) => {
  return FetchingData.match(uiNavigationData, {
    loading: () => (
      <StyledAvatar size={size}>
        <CircularProgress size="16px" />
      </StyledAvatar>
    ),
    error: () => <StyledAvatar size={size}>{"!"}</StyledAvatar>,
    success: ({ operatedAs, userDetails: { profileImgSrc, fullName } }) => {
      if (operatedAs) {
        if (size === "small")
          return (
            <OperateAsIcon
              sx={{
                color: "red !important",
              }}
            />
          );
        return (
          <Avatar
            sx={{
              backgroundColor: "white",
              color: "red",
            }}
          >
            <OperateAsIcon
              sx={{
                width: "2em",
                height: "2em",
              }}
            />
          </Avatar>
        );
      }
      return (
        <StyledAvatar
          size={size}
          {...(profileImgSrc !== null
            ? {
                src: profileImgSrc,
              }
            : {})}
        >
          {fullName[0]}
        </StyledAvatar>
      );
    },
  });
};
const OrcidIcon = () => (
  // biome-ignore lint/a11y/noSvgWithoutTitle: initial biome migration
  <svg xmlns="http://www.w3.org/2000/svg" version="1.1" viewBox="0 0 50 50">
    <g>
      <g id="Layer_1">
        <g>
          <path fill="#a6ce39" d="M49.3,25c0,13.4-10.9,24.3-24.3,24.3S.7,38.4.7,25,11.6.7,25,.7s24.3,10.9,24.3,24.3Z" />
          <g>
            <path fill="#fff" d="M17.1,36.1h-2.9V15.7h2.9v20.3Z" />
            <path
              fill="#fff"
              d="M21.4,15.7h7.9c7.5,0,10.8,5.4,10.8,10.2s-4.1,10.2-10.8,10.2h-7.9s0-20.4,0-20.4ZM24.3,33.4h4.7c6.6,0,8.1-5,8.1-7.5,0-4.1-2.6-7.5-8.3-7.5h-4.5v15.1h0Z"
            />
            <path fill="#fff" d="M17.5,11.5c0,1-.9,1.9-1.9,1.9s-1.9-.9-1.9-1.9.9-1.9,1.9-1.9c1.1,0,1.9.9,1.9,1.9Z" />
          </g>
        </g>
      </g>
    </g>
  </svg>
);
/**
 * Stable, untranslated identifiers for the main tabbed sections of the
 * product. Callers of the "page" variant pass one of these as `currentPage`
 * to mark the active tab; the AppBar maps each key to its translated label
 * (`appBar.sections.<key>.title`/`.subheader`).
 */
const TAB_KEYS = ["workspace", "gallery", "inventory", "system", "myRSpace"] as const;
export type TabKey = (typeof TAB_KEYS)[number];

const isTabKey = (page: string): page is TabKey => TAB_KEYS.some((key) => key === page);

type GalleryAppBarArgs = {
  /**
   * The app bar is used across the top of the pages that consistitute most of
   * the product, as wel as several dialogs. The behaviour of this component is
   * slightly different based on this, providing navigation only when `variant`
   * is "page".
   */
  variant: "page" | "dialog";

  /**
   * For the "page" variant, a {@link TabKey} marks the active tab. For the
   * "dialog" variant this is only shown as a heading, so any (already
   * translated) string may be passed.
   */
  currentPage: string;

  /**
   * Some pages have a sidebar that needs a toggle for opening and closing.
   * Those pages should pass an instance of SidebarToggle here.
   */
  sidebarToggle?: React.ReactElement<React.ComponentProps<typeof SidebarToggle>>;

  /**
   * Which accessibility tips to show based on the conformance the current
   * page/dialog has with the accessibility standard.
   * @see {@link module:AccessibilityTips} for more info.
   */
  accessibilityTips: {
    supportsReducedMotion?: boolean;
    supportsHighContrastMode?: boolean;
    supports2xZoom?: boolean;
    supportsSkipToContent?: boolean;
  };

  /*
   * A page of documentation to link to in the top right corner. If one is not
   * specified then the lighthouse dialog is opened allowing the user to browse
   * the documentation.
   */
  helpPage?: {
    docLink: URL;
    title: string;
  };

  /** Pass `true` inside a full-page SPA that already provides an ambient `I18nRoot`, to avoid nesting a second one. */
  ambientI18n?: boolean;
};
function GalleryAppBar({
  variant,
  currentPage,
  sidebarToggle,
  accessibilityTips,
  helpPage,
  ambientI18n = false,
}: GalleryAppBarArgs): React.ReactNode {
  const { t } = useTranslation("common");
  const theme = useTheme();
  const { isViewportSmall } = useViewportDimensions();
  const uiNavigationData = useUiNavigationData();
  const [appMenuAnchorEl, setAppMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  function handleAppMenuClose() {
    setAppMenuAnchorEl(null);
  }
  const [accountMenuAnchorEl, setAccountMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const [aboutDialogOpen, setAboutDialogOpen] = React.useState(false);
  const leftClipId = React.useId();
  const rightClipId = React.useId();
  const fetchedCurrentUser = useWhoAmI();
  const [brandingHref, setBrandingHref] = useSessionStorage<string | null>("brandingHref", null);
  React.useEffect(() => {
    FetchingData.getSuccessValue(uiNavigationData).do(({ bannerImgSrc }) => {
      setBrandingHref(bannerImgSrc);
    });
  }, [uiNavigationData]);
  const { showInventory, showSystem, showMyLabGroups } = FetchingData.getSuccessValue(uiNavigationData)
    .map(({ visibleTabs: { inventory, system, myLabGroups } }) => ({
      showInventory: Boolean(inventory),
      showSystem: Boolean(system),
      showMyLabGroups: Boolean(myLabGroups),
    }))
    .orElse({
      showInventory: false,
      showSystem: false,
      showMyLabGroups: false,
    });

  const isTabbedPage = isTabKey(currentPage);

  const sectionLabels: Record<TabKey, { title: string; subheader: string }> = {
    workspace: { title: t("appBar.sections.workspace.title"), subheader: t("appBar.sections.workspace.subheader") },
    gallery: { title: t("appBar.sections.gallery.title"), subheader: t("appBar.sections.gallery.subheader") },
    inventory: { title: t("appBar.sections.inventory.title"), subheader: t("appBar.sections.inventory.subheader") },
    myRSpace: { title: t("appBar.sections.myRSpace.title"), subheader: t("appBar.sections.myRSpace.subheader") },
    system: { title: t("appBar.sections.system.title"), subheader: t("appBar.sections.system.subheader") },
  };

  const displayPage = (page: string): string => (isTabKey(page) ? sectionLabels[page].title : page);

  const appBar = (
    <AppBar position="relative" aria-label={variant === "page" ? t("appBar.pageHeader") : t("appBar.dialogHeader")}>
      <Toolbar variant="dense">
        {variant === "page" && !isViewportSmall && (
          <>
            <Box
              sx={{
                height: "40px",
                backgroundColor: "white",
                width: "4px",
                clipPath: `url(#${leftClipId})`,
                marginLeft: "4px",
                marginRight: "-1px",
              }}
            ></Box>
            {/** biome-ignore lint/a11y/noSvgWithoutTitle: initial biome migration */}
            <svg width="0" height="0" viewBox="0 0 3.9 40">
              <defs>
                <clipPath id={leftClipId}>
                  <path d="M3.9,40C1.7,40,0,38.3,0,36.1V3.9C0,1.7,1.7,0,3.9,0v40Z" fill="#000000" />
                </clipPath>
              </defs>
            </svg>
          </>
        )}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            backgroundColor: variant === "page" && !isViewportSmall ? "white" : "unset",
            height: "40px",
            px: 0.5,
            gap: 0.5,
          }}
        >
          {sidebarToggle}
          {variant === "page" && !isViewportSmall && (
            <Box
              sx={{
                height: "36px",
                ml: 0.5,
                py: 0.25,
                position: "relative",
              }}
            >
              {Result.fromNullable(brandingHref, new Error("branding not cached"))
                .orElseTry(() => FetchingData.getSuccessValue(uiNavigationData).map(({ bannerImgSrc }) => bannerImgSrc))
                .map((href) => (
                  <img
                    key="branding small"
                    src={href}
                    alt={t("appBar.brandingAlt")}
                    style={{
                      height: "100%",
                    }}
                  />
                ))
                .orElse(null)}
            </Box>
          )}
        </Box>
        {variant === "page" && !isViewportSmall && (
          <>
            <Box
              sx={{
                height: "40px",
                backgroundColor: "white",
                width: "12px",
                clipPath: `url(#${rightClipId})`,
                marginLeft: "-1px",
              }}
            ></Box>
            {/** biome-ignore lint/a11y/noSvgWithoutTitle: initial biome migration */}
            <svg width="0" height="0">
              <defs>
                <clipPath id={rightClipId}>
                  <path d="M0,0c2.1,0,4.2,1.7,4.6,3.9l5.7,32.2c.4,2.1-1.1,3.9-3.2,3.9H0V0Z" fill="#000000" />
                </clipPath>
              </defs>
            </svg>
          </>
        )}
        {variant === "page" && isTabbedPage && (
          <VisuallyHiddenHeading variant="h1">{displayPage(currentPage)}</VisuallyHiddenHeading>
        )}
        {variant === "dialog" && (
          <Box
            sx={{
              ml: 0.5,
            }}
          >
            <Typography variant="h6" noWrap component="h2">
              {displayPage(currentPage)}
            </Typography>
          </Box>
        )}
        {!isViewportSmall && variant === "page" && (
          <Stack
            direction="row"
            spacing={2}
            sx={{
              mx: 1,
            }}
            component="nav"
            aria-label={t("appBar.mainLinks")}
          >
            <Link target="_self" aria-current={currentPage === "workspace" ? "page" : false} href="/workspace">
              {sectionLabels.workspace.title}
            </Link>
            <Link target="_self" aria-current={currentPage === "gallery" ? "page" : false} href="/gallery">
              {sectionLabels.gallery.title}
            </Link>
            {showInventory && (
              <Link target="_self" aria-current={currentPage === "inventory" ? "page" : false} href="/inventory">
                {sectionLabels.inventory.title}
              </Link>
            )}
            <Link
              target="_self"
              aria-current={currentPage === "myRSpace" ? "page" : false}
              href={showMyLabGroups ? "/groups/viewPIGroup" : "/userform"}
            >
              {sectionLabels.myRSpace.title}
            </Link>
            {showSystem && (
              <Link target="_self" aria-current={currentPage === "system" ? "page" : false} href="/system">
                {sectionLabels.system.title}
              </Link>
            )}
          </Stack>
        )}
        {isViewportSmall && variant === "page" && (
          <>
            <List
              component="nav"
              aria-label={t("appBar.mainNavigation")}
              disablePadding
              sx={{
                ml: 1,
              }}
            >
              <ListItemButton
                dense
                id="app-menu-button"
                aria-haspopup="menu"
                aria-controls="app-menu"
                {...(appMenuAnchorEl
                  ? {
                      "aria-expanded": "true",
                    }
                  : {})}
                onClick={(event) => {
                  setAppMenuAnchorEl(event.currentTarget);
                }}
              >
                <ListItemText primary={isTabbedPage ? displayPage(currentPage) : t("appBar.goTo")} />
                <ListItemIcon>
                  <ArrowDropDownIcon />
                </ListItemIcon>
              </ListItemButton>
            </List>
            <Menu
              id="app-menu"
              anchorEl={appMenuAnchorEl}
              open={Boolean(appMenuAnchorEl)}
              onClose={handleAppMenuClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              sx={{
                [`.${menuClasses.paper}`]: {
                  /*
                   * Generally we don't add box shadows to menus, but we do add
                   * box shadows to popups opened from the app bar to make them
                   * hover over the page's content.
                   */
                  boxShadow:
                    "3px 5px 5px -3px rgba(0,0,0,0.2),0px 8px 10px 1px rgba(0,0,0,0.14),0px 3px 14px 2px rgba(0,0,0,0.12)",
                },
              }}
              slotProps={{
                list: {
                  "aria-labelledby": "app-menu-button",
                  disablePadding: true,
                },
              }}
            >
              <AccentMenuItem
                title={sectionLabels.workspace.title}
                avatar={<NotebookIcon />}
                subheader={sectionLabels.workspace.subheader}
                foregroundColor={WORKSPACE_COLOR.contrastText}
                backgroundColor={WORKSPACE_COLOR.main}
                onClick={() => {
                  window.location.href = "/workspace";
                  handleAppMenuClose();
                }}
                current={currentPage === "workspace" ? "page" : false}
              />
              <AccentMenuItem
                title={sectionLabels.gallery.title}
                avatar={<FileIcon />}
                subheader={sectionLabels.gallery.subheader}
                foregroundColor={GALLERY_COLOR.contrastText}
                backgroundColor={GALLERY_COLOR.main}
                onClick={() => {
                  window.location.href = "/gallery";
                  handleAppMenuClose();
                }}
                current={currentPage === "gallery" ? "page" : false}
              />
              {showInventory && (
                <AccentMenuItem
                  title={sectionLabels.inventory.title}
                  avatar={<FlaskIcon />}
                  subheader={sectionLabels.inventory.subheader}
                  foregroundColor={INVENTORY_COLOR.contrastText}
                  backgroundColor={INVENTORY_COLOR.main}
                  onClick={() => {
                    window.location.href = "/inventory";
                    handleAppMenuClose();
                  }}
                  current={currentPage === "inventory" ? "page" : false}
                />
              )}
              <AccentMenuItem
                title={sectionLabels.myRSpace.title}
                avatar={<ProfileIcon />}
                subheader={sectionLabels.myRSpace.subheader}
                foregroundColor={OTHER_COLOR.contrastText}
                backgroundColor={OTHER_COLOR.main}
                onClick={() => {
                  window.location.href = showMyLabGroups ? "/groups/viewPIGroup" : "/userform";
                  setAccountMenuAnchorEl(null);
                }}
                current={currentPage === "myRSpace" ? "page" : false}
              />
              {showSystem && (
                <AccentMenuItem
                  title={sectionLabels.system.title}
                  avatar={<SystemIcon />}
                  subheader={sectionLabels.system.subheader}
                  foregroundColor={SYSADMIN_COLOR.contrastText}
                  backgroundColor={SYSADMIN_COLOR.main}
                  onClick={() => {
                    window.location.href = "/system";
                    handleAppMenuClose();
                  }}
                  current={currentPage === "system" ? "page" : false}
                />
              )}
            </Menu>
          </>
        )}
        <Box
          sx={{
            flexGrow: 1,
          }}
        ></Box>
        {FetchingData.getSuccessValue(uiNavigationData)
          .map(({ nextMaintenance }) => nextMaintenance)
          .flatMap(Parsers.isNotNull)
          .map(({ startDate }) => <IncomingMaintenancePopup key="maintenance" startDate={startDate} />)
          .orElse(null)}
        {variant === "page" && (
          <>
            {FetchingData.getSuccessValue(fetchedCurrentUser)
              .map((currentUser) => <NotificationCounter key="notification counter" currentUser={currentUser} />)
              .orElse(null)}
            <Box
              sx={{
                ml: 1,
              }}
            >
              <IconButtonWithTooltip
                size="small"
                disabled={FetchingData.isLoading(uiNavigationData)}
                onClick={(event) => {
                  setAccountMenuAnchorEl(event.currentTarget);
                }}
                icon={<DynamicAvatar uiNavigationData={uiNavigationData} size="small" />}
                title={t("appBar.accountMenu")}
                id="account-menu-button"
                aria-haspopup="menu"
                aria-controls="account-menu"
                {...(accountMenuAnchorEl
                  ? {
                      "aria-expanded": "true",
                    }
                  : {})}
              />
              <Menu
                id="account-menu"
                anchorEl={accountMenuAnchorEl}
                open={Boolean(accountMenuAnchorEl)}
                onClose={() => {
                  setAccountMenuAnchorEl(null);
                }}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                sx={{
                  [`.${menuClasses.paper}`]: {
                    boxShadow:
                      "3px 5px 5px -3px rgba(0,0,0,0.2),0px 8px 10px 1px rgba(0,0,0,0.14),0px 3px 14px 2px rgba(0,0,0,0.12)",
                  },
                }}
                slotProps={{
                  list: {
                    "aria-labelledby": "account-menu-button",
                    disablePadding: true,
                    sx: {
                      pt: 0.5,
                    },
                  },
                }}
              >
                {FetchingData.match(uiNavigationData, {
                  loading: () => null,
                  error: (errorMsg) => (
                    <ListItem>
                      <ListItemText primary={t("appBar.errorLoadingDetails")} secondary={errorMsg} />
                    </ListItem>
                  ),
                  success: ({ userDetails }) => (
                    <ListItem
                      key="user details"
                      sx={{
                        py: 0,
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          alignSelf: "flex-start",
                          mt: 1,
                        }}
                      >
                        <DynamicAvatar uiNavigationData={uiNavigationData} />
                      </ListItemIcon>
                      <Stack>
                        <ListItemText
                          sx={{
                            mt: 0.5,
                            ml: 1.6,
                          }}
                          primary={
                            <>
                              {FetchingData.getSuccessValue(uiNavigationData)
                                .map(({ operatedAs }) => operatedAs)
                                .flatMap(Parsers.isTrue)
                                .map(() => (
                                  <>
                                    <strong>{t("appBar.operatingAs")}</strong>
                                    <br />
                                  </>
                                ))
                                .orElse(null)}
                              {`${userDetails.fullName} (${userDetails.username})`}
                            </>
                          }
                          secondary={userDetails.email}
                        />
                        {userDetails.orcidAvailable && (
                          <ListItemText
                            /*
                             * The styling of this component is dictated by the ORCID display guidelines
                             * https://info.orcid.org/documentation/integration-guide/orcid-id-display-guidelines/#Compact_ORCID_iD
                             */
                            sx={{
                              mt: -0.5,
                            }}
                            primary={
                              userDetails.orcidId === null ? (
                                <TransRichText i18nKey="common:appBar.orcidAdd" />
                              ) : (
                                <Stack
                                  direction="row"
                                  spacing={0.5}
                                  sx={{
                                    alignItems: "center",
                                  }}
                                >
                                  <SvgIcon>
                                    <OrcidIcon />
                                  </SvgIcon>
                                  <span>{userDetails.orcidId}</span>
                                </Stack>
                              )
                            }
                            slotProps={{
                              primary: {
                                sx: {
                                  fontFamily: userDetails.orcidId === null ? "inherit" : "monospace",
                                  lineHeight: userDetails.orcidId === null ? "unset" : "1em",
                                  fontSize: "0.8em",
                                  alignItems: "center",
                                  textDecoration: userDetails.orcidId === null ? "none" : "underline",
                                },
                              },
                            }}
                          />
                        )}
                      </Stack>
                    </ListItem>
                  ),
                })}
                <AccentMenuItem
                  title={t("appBar.messaging")}
                  avatar={<MessageIcon />}
                  compact
                  onClick={() => {
                    setAccountMenuAnchorEl(null);
                  }}
                  component="a"
                  href="/dashboard"
                />
                <AccentMenuItem
                  title={t("appBar.apps")}
                  avatar={<AppsIcon />}
                  compact
                  onClick={() => {
                    setAccountMenuAnchorEl(null);
                  }}
                  component="a"
                  href="/apps"
                />
                {FetchingData.getSuccessValue(uiNavigationData)
                  .map(({ visibleTabs: { published } }) => published)
                  .flatMap(Parsers.isTrue)
                  .map(() => (
                    <AccentMenuItem
                      key="published"
                      title={t("appBar.published")}
                      avatar={<PublicIcon />}
                      compact
                      onClick={(e) => {
                        e.preventDefault();
                        setAccountMenuAnchorEl(null);
                        window.open("/public/publishedView/publishedDocuments", "_blank", "noopener,noreferrer");
                      }}
                      component="a"
                      href="/public/publishedView/publishedDocuments"
                    />
                  ))
                  .orElse(null)}
                <AccessibilityTipsMenuItem
                  {...(accessibilityTips ?? {})}
                  onClose={() => {
                    setAccountMenuAnchorEl(null);
                  }}
                />
                <AccentMenuItem
                  title={t("appBar.aboutRSpace")}
                  avatar={<InfoIcon />}
                  compact
                  onClick={() => {
                    setAccountMenuAnchorEl(null);
                    setAboutDialogOpen(true);
                  }}
                />
                {FetchingData.getSuccessValue(uiNavigationData)
                  .map(({ operatedAs }) => operatedAs)
                  .flatMap(Parsers.isTrue)
                  .map(() => (
                    <AccentMenuItem
                      key="release"
                      title={t("appBar.release")}
                      avatar={<LogoutIcon />}
                      backgroundColor={lighten(theme.palette.error.light, 0.5)}
                      foregroundColor={darken(theme.palette.error.dark, 0.3)}
                      compact
                      onClick={() => {
                        JwtService.destroyToken();
                        setAccountMenuAnchorEl(null);
                        window.location.href = "/logout/runAsRelease";
                      }}
                    />
                  ))
                  .orElse(
                    <AccentMenuItem
                      title={t("appBar.logOut")}
                      avatar={<LogoutIcon />}
                      backgroundColor={lighten(theme.palette.error.light, 0.5)}
                      foregroundColor={darken(theme.palette.error.dark, 0.3)}
                      compact
                      onClick={() => {
                        JwtService.destroyToken();

                        /*
                         * On some servers the user can login with the Google
                         * Login workflow. On those servers, `gapi` will be
                         * defined globally by header.jsp
                         */
                        if (typeof window.gapi !== "undefined" && window.gapi.auth2) {
                          const auth2 = window.gapi.auth2.getAuthInstance();
                          if (auth2) {
                            void auth2.signOut().then(() => {
                              console.log("User signed out.");
                            });
                          } else {
                            console.log("No GAPI authinstance defined");
                          }
                        } else {
                          console.log("No GAPI defined");
                        }
                        setAccountMenuAnchorEl(null);
                        window.location.href = "/logout";
                      }}
                    />,
                  )}
                {FetchingData.getSuccessValue(uiNavigationData)
                  .map(({ bannerImgSrc }) => (
                    <ListItem
                      key="branding large"
                      sx={{
                        py: 0,
                        mb: 1,
                        justifyContent: "flex-end",
                      }}
                    >
                      <img
                        src={bannerImgSrc}
                        alt={t("appBar.brandingAlt")}
                        style={{
                          maxWidth: "120px",
                          display: "block",
                        }}
                      />
                    </ListItem>
                  ))
                  .orElse(null)}
              </Menu>
              <AboutRSpaceDialog open={aboutDialogOpen} onClose={() => setAboutDialogOpen(false)} />
            </Box>
          </>
        )}
        {variant === "dialog" && (
          <Box
            sx={{
              ml: 1,
            }}
          >
            <AccessibilityTipsIconButton {...(accessibilityTips ?? {})} />
          </Box>
        )}
        <Box
          sx={{
            ml: 1,
          }}
        >
          {helpPage ? (
            <Box
              sx={{
                transform: "translateY(2px)",
              }}
            >
              <HelpLinkIcon title={helpPage.title} link={helpPage.docLink} />
            </Box>
          ) : (
            <HelpDocs />
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );

  return ambientI18n ? (
    appBar
  ) : (
    <I18nRoot namespaces={["common", "about"]} fallback={<Skeleton variant="rectangular" height={48} />}>
      {appBar}
    </I18nRoot>
  );
}

export default observer(GalleryAppBar);
